import React from 'react'
import styled from '@emotion/styled'

import { BACKGROUND_CREDIT_CHECK_PROVIDER } from 'manager/pages/ExperianAuth/interfaces'
import type { Application } from 'renter/interfaces/api/application'
import ApplicantInfo from 'shared/components/CreditAndBackgroundCheck/components/ApplicantInfo'
import CreditReport from 'shared/components/CreditAndBackgroundCheck/components/CreditReport'
import CreditScore from 'shared/components/CreditAndBackgroundCheck/components/CreditScore'
import Evictions from 'shared/components/CreditAndBackgroundCheck/components/Evictions'
import ExpiredInfo from 'shared/components/CreditAndBackgroundCheck/components/ExpiredReport'
import PublicRecords from 'shared/components/CreditAndBackgroundCheck/components/PublicRecords'
import StateCriminalRecords from 'shared/components/CreditAndBackgroundCheck/components/StateCriminalRecords'
import ReportDetails from 'shared/components/CreditAndBackgroundCheck/components/ReportDetails'
import PendingReportsBanner from 'shared/components/CreditAndBackgroundCheck/components/PendingReportsBanner'
import { WarningBanner } from './styled'
import Icon from 'shared/components/Icon'
import {
  APPLICATION_COUNTY_CRIMINAL_SEARCH_STATUS,
  APPLICATION_STATUSES,
  colors,
  COLORS,
} from 'shared/config/constants'
import { isManagerApp } from 'shared/utils/auth'
import { isDemo, isTest, isDevelopment } from 'shared/utils/platform'

import {
  CountyCriminalRecordsProps,
  CreditScoreSummary,
  CriminalRecordsType,
  EvictionsType,
} from './interfaces'
import { AddressPartial, DealPartial } from './types'

// icon

export const HelpIcon = styled(Icon.Help)`
  display: block;
  width: 16px;
  height: 16px;
  color: ${colors.accent[500]};
`

const defaultCreditScoreSummary: Partial<{
  firstName: string
  middleName: string
  lastName: string
  addresses: AddressPartial[]
}> = {
  firstName: null,
  middleName: null,
  lastName: null,
  addresses: [{ address: null, city: null, state: null, zipcode: null }],
}

type CreditAndBackgroundCheckProps = {
  hideCriminalRecords: boolean
  isEvictions?: boolean
  canAccessCountyCriminalRecords?: boolean
  countyCriminalRecords?: CountyCriminalRecordsProps
  countyCriminalSearchStatus?: typeof APPLICATION_COUNTY_CRIMINAL_SEARCH_STATUS
  noSSNreasonToStay?: string
  hasCountyCriminalSearch?: boolean
  hasTuCountyCriminalSearch?: boolean
  deal?: DealPartial | null
  application?: Application | null
  backgroundCreditCheck?: {
    backgroundId?: string
    transactionId?: string
    backgroundAndCreditCheckId?: number
    isComplete?: boolean
    creditScoreSummary?: CreditScoreSummary
    criminalRecords?: CriminalRecordsType
    evictionRecords?: EvictionsType
  }
}

const N_A = 'N/A'

const CreditAndBackgroundCheck = ({
  hideCriminalRecords,
  isEvictions,
  canAccessCountyCriminalRecords,
  countyCriminalRecords,
  countyCriminalSearchStatus,
  noSSNreasonToStay,
  hasCountyCriminalSearch,
  hasTuCountyCriminalSearch,
  deal = null,
  application = null,
  backgroundCreditCheck,
}: CreditAndBackgroundCheckProps) => {
  const creditScoreSummary = backgroundCreditCheck?.creditScoreSummary
  const criminalRecords = backgroundCreditCheck?.criminalRecords
  const evictions = backgroundCreditCheck?.evictionRecords
  const { firstName, middleName, lastName } =
    creditScoreSummary ?? defaultCreditScoreSummary

  const applicantFullName =
    !!firstName || !!lastName
      ? [firstName, middleName, lastName].filter(Boolean).join(' ')
      : N_A

  const providerIsExperian = application?.bccProvider === BACKGROUND_CREDIT_CHECK_PROVIDER.EXPERIAN_CONSUMER_CONNECT
  const isExpired = providerIsExperian && creditScoreSummary?.expired
  const { dealDecisionIsMade, status } = application
  const isSubmitted = APPLICATION_STATUSES.submitted === application.status
  const displayDisclaimerBanner = dealDecisionIsMade && isSubmitted && isExpired && isManagerApp()

  return isExpired ? (
    <>
      {displayDisclaimerBanner && (
        <WarningBanner>
          <Icon.DarkOrangeExclamationHollow width={24} height={24} />
          <span className="banner-message">
            Background and credit report information from a submitted application, once a decision is made, is restricted under our license to that specific application and cannot be used for future leasing decisions.
          </span>
        </WarningBanner>
      )}
      <ExpiredInfo creditScoreSummary={creditScoreSummary} />
    </>
  ) : (
    <>
     <PendingReportsBanner
        creditScoreSummary={creditScoreSummary}
        criminalRecords={criminalRecords}
        evictions={evictions}
      />
      {providerIsExperian && (
        <ReportDetails
          creditScoreSummary={creditScoreSummary}
          criminalRecords={criminalRecords}
          evictions={evictions}
          backgroundMeta={{
            backgroundId: backgroundCreditCheck?.backgroundId,
            transactionId: backgroundCreditCheck?.transactionId,
            backgroundAndCreditCheckId: backgroundCreditCheck?.backgroundAndCreditCheckId,
          }}
        />
      )}
      <ApplicantInfo
        creditScoreSummary={creditScoreSummary}
        deal={deal}
        applicantFullName={applicantFullName}
        application={application}
      />

      <CreditScore
        creditScoreSummary={creditScoreSummary}
        criminalRecords={criminalRecords}
        hideCriminalRecords={hideCriminalRecords}
        isEvictions={isEvictions}
        evictions={evictions}
        noSSNreasonToStay={noSSNreasonToStay}
      />

      <CreditReport
        creditScoreSummary={creditScoreSummary}
        applicantFullName={applicantFullName}
      />

      <PublicRecords creditScoreSummary={creditScoreSummary} />

      <StateCriminalRecords
        canAccessCountyCriminalRecords={canAccessCountyCriminalRecords}
        countyCriminalRecords={countyCriminalRecords}
        countyCriminalSearchStatus={countyCriminalSearchStatus}
        criminalRecords={criminalRecords}
        hasCountyCriminalSearch={hasCountyCriminalSearch}
        hasTuCountyCriminalSearch={hasTuCountyCriminalSearch}
        hideCriminalRecords={hideCriminalRecords}
      />

      <Evictions evictions={evictions} isEvictions={isEvictions} application={application} />
    </>
  )
}

export default CreditAndBackgroundCheck
